/**
 * Project Brief — shared document model.
 * --------------------------------------------------------------------------
 * ONE normalisation of the Project Lab workspace payload, consumed by every
 * presentation of the brief:
 *
 *   /project-lab/student/projects/:enrollmentId
 *        │
 *        ├── the responsive workspace UI
 *        └── buildProjectBriefModel()
 *                 ├── printable document (ProjectBriefPrintable)
 *                 └── PDF (projectBriefPdf)
 *
 * No project, section, stage or item content is invented, reordered or stored
 * here — the server's order is preserved exactly. If the workspace payload has
 * no authored content, the model comes back empty and the callers say so
 * rather than printing a skeleton.
 */

const MODE_LABEL = {
  acknowledge: 'Review & acknowledge',
  checklist: 'Checklist completion',
  submission: 'Submission reviewed by mentor',
}

function clean(value) {
  if (value === null || value === undefined) return null
  const s = String(value).trim()
  return s ? s : null
}

function listOf(value) {
  if (!Array.isArray(value)) return []
  return value.map(clean).filter(Boolean)
}

/** "acknowledge" → "Review & acknowledge" */
export function completionModeLabel(mode) {
  return MODE_LABEL[mode] || 'Stage'
}

/**
 * Build the printable/exportable brief from the workspace payload.
 *
 * @param {object} projectData  the response of the student workspace endpoint
 * @returns {object|null} model, or null when there is nothing to document
 */
export function buildProjectBriefModel(projectData) {
  if (!projectData || !projectData.project) return null

  const project = projectData.project || {}
  const enrollment = projectData.enrollment || {}
  const mentor = projectData.mentor || null
  const sections = Array.isArray(projectData.brief?.sections) ? projectData.brief.sections : []
  const stages = Array.isArray(projectData.stages) ? projectData.stages : []

  const meta = [
    { label: 'Discipline', value: clean(project.discipline) },
    { label: 'Level', value: clean(project.level) },
    { label: 'Duration', value: clean(project.duration) },
    { label: 'Mode', value: clean(enrollment.mode) },
    { label: 'Stages', value: stages.length ? String(stages.length) : null },
    { label: 'Mentor', value: mentor ? clean(`${mentor.first_name || ''} ${mentor.last_name || ''}`) : null },
  ].filter((m) => m.value)

  return {
    title: clean(project.title) || 'Project Brief',
    summary: clean(project.short_summary),
    finalDeliverable: clean(project.final_deliverable),
    meta,
    sections: sections
      .map((s) => ({ title: clean(s.title), content: clean(s.content) }))
      .filter((s) => s.title || s.content),
    stages: stages.map((s, i) => ({
      number: s.stage_index ?? i + 1,
      title: clean(s.title) || `Stage ${s.stage_index ?? i + 1}`,
      modeLabel: completionModeLabel(s.completion_mode),
      objective: clean(s.objective),
      description: clean(s.description),
      guidance: listOf(s.guidance),
      requirements: listOf(s.requirements),
      acceptance: listOf(s.acceptance_criteria),
      submission: s.requires_files
        ? {
            maxFiles: s.max_files || null,
            acceptedTypes: clean(s.accepted_file_types),
            notesRequired: !!s.submission_notes_required,
          }
        : null,
    })),
    generatedAt: new Date(),
  }
}

/** Safe, descriptive file name: "short-documentary-project-brief.pdf" */
export function briefFileName(model, extension = 'pdf') {
  const base = (model?.title || 'project')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
  return `${base || 'project'}-brief.${extension}`
}

/** Does this model contain anything worth exporting? */
export function briefHasContent(model) {
  if (!model) return false
  return model.sections.length > 0 || model.stages.length > 0
}
